/**
 * PSE-2 — Local government meeting-minutes sentiment fetcher.
 *
 * Three sub-fetchers (each is its own HTML-scrape pattern):
 *  1. MuniCode  — `https://library.municode.com/search?...`
 *  2. Granicus  — `https://{client}.granicus.com/MinutesViewer.php?...`
 *  3. CivicPlus — `https://www.{city}.gov/AgendaCenter`
 *
 * Why three: the three vendors collectively host 90%+ of US municipal
 * meeting-minute archives. None of them expose a public JSON API; each
 * has its own HTML structure. The orchestrator treats them as a single
 * logical source (`'local-minutes'`) so a partial outage on one vendor
 * doesn't flip the whole source to `unavailable`.
 *
 * Architecture notes:
 *  - Per-state client-id registry: `MUNICODE_CLIENT_IDS` maps a USPS
 *    state code → the MuniCode `clientId` for that state's marquee
 *    cities. Five states seeded (LA, TX, NY, CA, AZ) — full coverage
 *    is a `CAF-FOLLOWUP-` job; unmapped states get a graceful empty
 *    result, NOT a hard error.
 *  - Sentiment scoring: keyword polarity from `sentimentPolarity.ts`.
 *    Council minutes use distinctive vote-disposition language ("motion
 *    carries", "motion fails", "denied", "approved") — the keyword
 *    list there was tuned for exactly this corpus.
 *  - HTML-shape-change tolerance: every regex-based extractor returns
 *    `[]` rather than throwing when the shape changes upstream. The
 *    orchestrator's `partial` status surfaces the degradation without
 *    breaking the SED build.
 *  - Telemetry: failures log a `[pse:local-minutes:{vendor}]`
 *    breadcrumb (the existing dataProvider convention) so Cloud Run
 *    logs can bucket vendor-specific outages.
 *
 * NEVER throws. Returns `{ status: 'ok' | 'unavailable', entries }`.
 */
import type { PublicSentimentEntry } from '../../types/siteEvidence';
import type { PublicSentimentSourceResult } from '../publicSentimentOrchestrator';
import { fetchWithTimeoutCircuit } from './pseFetchCircuit';
import { rethrowIfAborted, throwIfAborted } from '../../lib/abortSignal';
import {
  classifyBtmTech,
  lengthBasedConfidence,
  polarityScore,
  truncateSnippet,
} from './sentimentPolarity';

/* ── Per-state MuniCode client-id registry ─────────────────────────── */

/**
 * USPS state codes the platform actively supports for MuniCode lookups.
 * Adding a state requires both an entry here AND a regulatory.data.state
 * value that matches. See `CAF-FOLLOWUP-MUNICODE-EXPAND` for the queued
 * work to fill out the remaining 45 states.
 */
type USStateCode =
  | 'AL' | 'AK' | 'AZ' | 'AR' | 'CA' | 'CO' | 'CT' | 'DE' | 'FL' | 'GA'
  | 'HI' | 'ID' | 'IL' | 'IN' | 'IA' | 'KS' | 'KY' | 'LA' | 'ME' | 'MD'
  | 'MA' | 'MI' | 'MN' | 'MS' | 'MO' | 'MT' | 'NE' | 'NV' | 'NH' | 'NJ'
  | 'NM' | 'NY' | 'NC' | 'ND' | 'OH' | 'OK' | 'OR' | 'PA' | 'RI' | 'SC'
  | 'SD' | 'TN' | 'TX' | 'UT' | 'VT' | 'VA' | 'WA' | 'WV' | 'WI' | 'WY';

/**
 * Per-state MuniCode client identifier. MuniCode uses opaque numeric
 * client-ids on its search URL; these are the seed five from the PSE-2
 * pack brief.
 *
 * **CAF-FOLLOWUP-MUNICODE-EXPAND** — populate the remaining 45 states by
 * scraping the MuniCode public state index (one-time pass; the ids are
 * stable). Until then, unmapped states get an empty result rather
 * than a wrong-state search.
 *
 * The values here are illustrative seeds matching the public MuniCode
 * directory structure as of 2026-05 — they're not authoritative API
 * contract values; MuniCode's UI maps these to the actual archives.
 */
export const MUNICODE_CLIENT_IDS: Partial<Record<USStateCode, string>> = {
  LA: 'LA-STATE',  // CAF-FOLLOWUP-MUNICODE-EXPAND
  TX: 'TX-STATE',  // CAF-FOLLOWUP-MUNICODE-EXPAND
  NY: 'NY-STATE',  // CAF-FOLLOWUP-MUNICODE-EXPAND
  CA: 'CA-STATE',  // CAF-FOLLOWUP-MUNICODE-EXPAND
  AZ: 'AZ-STATE',  // CAF-FOLLOWUP-MUNICODE-EXPAND
};

/**
 * The seed ids above are ILLUSTRATIVE (see the registry comment) — they
 * are not real MuniCode client ids, and a search issued with one hangs
 * until the 15s timeout (observed 2026-07-28 on an LA site: every
 * municode request in the run timed out; the vendor can never succeed
 * through a placeholder). Until CAF-FOLLOWUP-MUNICODE-EXPAND supplies
 * verified ids, placeholder states take the same graceful-empty path as
 * unmapped states — an honest no-coverage, not a guaranteed timeout.
 */
const MUNICODE_PLACEHOLDER_IDS = new Set(
  Object.values(MUNICODE_CLIENT_IDS).filter(id => id.endsWith('-STATE')),
);

/* ── MuniCode fetcher ──────────────────────────────────────────────── */

const MUNICODE_BASE = 'https://library.municode.com/search';

/**
 * Build a MuniCode search URL. Returns null when no client-id is known
 * for `state` — the caller skips the fetch gracefully.
 */
function buildMuniCodeUrl(state: string, query: string): string | null {
  const clientId = MUNICODE_CLIENT_IDS[state as USStateCode];
  if (!clientId || MUNICODE_PLACEHOLDER_IDS.has(clientId)) return null;
  const params = new URLSearchParams({
    stateId: state,
    clientId,
    searchText: query,
  });
  return `${MUNICODE_BASE}?${params.toString()}`;
}

/**
 * Extract result blocks from a MuniCode HTML response. Best-effort
 * regex matcher — MuniCode renders search results inside `.search-result`
 * divs with a `data-title` and a snippet paragraph; we pull title +
 * permalink + 200-char excerpt.
 *
 * Returns `[]` when the HTML doesn't match the expected shape (vendor
 * UI change). The orchestrator routes that to `partial` via the
 * source-failure path.
 */
export function parseMuniCodeHtml(html: string): Array<{ title: string; url: string; excerpt: string }> {
  if (typeof html !== 'string' || html.length === 0) return [];
  const out: Array<{ title: string; url: string; excerpt: string }> = [];
  // Match repeated `<div class="search-result">…</div>` blocks. MuniCode
  // template puts `<a class="title" href="…">…</a>` + `<p class="excerpt">…</p>`
  // inside each. The regex is greedy across attributes but non-greedy
  // across the block to handle nested HTML.
  const blockPattern = /<div\s+class="search-result"[^>]*>([\s\S]*?)<\/div>\s*(?=<div\s+class="search-result"|$)/g;
  let match: RegExpExecArray | null;
  while ((match = blockPattern.exec(html)) !== null) {
    const block = match[1];
    const titleMatch = block.match(/<a\s+class="title"\s+href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/);
    if (!titleMatch) continue;
    const url = titleMatch[1].trim();
    const title = titleMatch[2].replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    const excerptMatch = block.match(/<p\s+class="excerpt"[^>]*>([\s\S]*?)<\/p>/);
    const excerpt = excerptMatch
      ? excerptMatch[1].replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim()
      : '';
    if (url && title) out.push({ title, url, excerpt });
  }
  return out;
}

async function fetchMuniCodeOne(
  state: string,
  query: string,
  signal?: AbortSignal,
): Promise<PublicSentimentEntry[]> {
  const url = buildMuniCodeUrl(state, query);
  if (!url) return [];
  const res = await fetchWithTimeoutCircuit('municode', url, 15_000, signal);
  if (!res.ok) throw new Error(`municode: HTTP ${res.status}`);
  const html = await res.text();
  const blocks = parseMuniCodeHtml(html);
  return blocks.map(b => buildEntry({
    sourceId: 'local-minutes',
    title: b.title,
    excerpt: b.excerpt,
    url: b.url.startsWith('http') ? b.url : `https://library.municode.com${b.url}`,
  }));
}

/* ── Granicus fetcher ──────────────────────────────────────────────── */

/**
 * Granicus uses per-client subdomains (`{client}.granicus.com`) for
 * each municipality. Their MinutesViewer endpoint accepts `meeting_id`
 * + optional `q` for full-text search. We compose the URL from the
 * `client` slug (lowercased city name in most cases) + query.
 *
 * Unlike MuniCode there's no central index — discovering the client
 * slug is part of the per-site setup. PSE-2 ships with a small
 * client-resolver fallback: when no `granicusClient` is supplied, we
 * skip Granicus for that locality (returns empty). The
 * `CAF-FOLLOWUP-GRANICUS-RESOLVER` pack adds the per-municipality
 * lookup.
 */
function buildGranicusUrl(client: string, query: string): string | null {
  if (!client || /[^a-z0-9-]/i.test(client)) return null;
  // Defensive: the subdomain MUST be a single-label slug; we don't
  // accept hosts with dots so a hostile caller can't redirect us to a
  // sibling host.
  const params = new URLSearchParams({ q: query });
  return `https://${client.toLowerCase()}.granicus.com/MinutesViewer.php?${params.toString()}`;
}

/**
 * Extract result rows from a Granicus MinutesViewer HTML response. The
 * template renders results in a `<table class="results">` with one
 * `<tr>` per minute; each row has a title anchor + a 1-2 line excerpt
 * in a sibling td.
 *
 * Returns `[]` when the table is missing (vendor UI change).
 */
export function parseGranicusHtml(html: string): Array<{ title: string; url: string; excerpt: string }> {
  if (typeof html !== 'string' || html.length === 0) return [];
  const out: Array<{ title: string; url: string; excerpt: string }> = [];
  // Pull all rows inside the results table.
  const tableMatch = html.match(/<table[^>]*class="[^"]*results[^"]*"[^>]*>([\s\S]*?)<\/table>/);
  if (!tableMatch) return [];
  const rowPattern = /<tr[^>]*>([\s\S]*?)<\/tr>/g;
  let match: RegExpExecArray | null;
  while ((match = rowPattern.exec(tableMatch[1])) !== null) {
    const row = match[1];
    const titleMatch = row.match(/<a[^>]+href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/);
    if (!titleMatch) continue;
    const url = titleMatch[1].trim();
    const title = titleMatch[2].replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    // Excerpt = second <td>'s text content if present.
    const excerptMatch = row.match(/<td[^>]*class="[^"]*excerpt[^"]*"[^>]*>([\s\S]*?)<\/td>/);
    const excerpt = excerptMatch
      ? excerptMatch[1].replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim()
      : '';
    if (url && title) out.push({ title, url, excerpt });
  }
  return out;
}

async function fetchGranicusOne(
  client: string | undefined,
  query: string,
  signal?: AbortSignal,
): Promise<PublicSentimentEntry[]> {
  if (!client) return [];
  const url = buildGranicusUrl(client, query);
  if (!url) return [];
  const res = await fetchWithTimeoutCircuit('granicus', url, 15_000, signal);
  if (!res.ok) throw new Error(`granicus: HTTP ${res.status}`);
  const html = await res.text();
  const blocks = parseGranicusHtml(html);
  return blocks.map(b => buildEntry({
    sourceId: 'local-minutes',
    title: b.title,
    excerpt: b.excerpt,
    url: b.url.startsWith('http') ? b.url : `https://${client.toLowerCase()}.granicus.com${b.url}`,
  }));
}

/* ── CivicPlus fetcher ─────────────────────────────────────────────── */

/**
 * CivicPlus AgendaCenter pages live under `https://www.{city}.gov/AgendaCenter`.
 * The page renders a table of meetings with permalinks and brief
 * descriptions. We accept the `cityHost` argument verbatim (must be a
 * pre-validated slug) and append `/AgendaCenter`.
 *
 * Like Granicus, there's no central index; discovering the host is part
 * of per-site setup. Missing `cityHost` → graceful empty.
 */
function buildCivicPlusUrl(cityHost: string): string | null {
  if (!cityHost) return null;
  // Defensive: reject hosts containing protocol / paths / spaces. The
  // proxy allowlist will reject anything we get wrong anyway, but
  // pre-filtering keeps the failure modes clean.
  if (!/^[a-z0-9.-]+$/i.test(cityHost)) return null;
  return `https://${cityHost.toLowerCase()}/AgendaCenter`;
}

/**
 * Extract minute rows from a CivicPlus AgendaCenter HTML response. The
 * template uses a `<table class="centerListing">` with rows that each
 * link to a minutes PDF.
 *
 * Returns `[]` when the listing isn't found.
 */
export interface CivicAgendaRecord {
  title: string; url: string; excerpt: string; eventDate?: string; kind?: 'agenda' | 'minutes';
}
export function parseCivicPlusHtml(html: string, baseUrl: string): CivicAgendaRecord[] {
  if (typeof html !== 'string' || html.length === 0) return [];
  const out: CivicAgendaRecord[] = [];
  const tables = [...html.matchAll(/<table\b([^>]*)>([\s\S]*?)<\/table>/gi)]
    .filter(match => /centerListing|List of Agendas/i.test(match[1]!));
  if (!tables.length) return [];
  const rowPattern = /<tr[^>]*>([\s\S]*?)<\/tr>/g;
  let match: RegExpExecArray | null;
  const seen = new Set<string>();
  while ((match = rowPattern.exec(tables.map(table => table[2]).join('\n'))) !== null) {
    const row = match[1];
    const civicLinks = [...row.matchAll(/<a\b[^>]*href="([^"]*\/AgendaCenter\/ViewFile\/(Agenda|Minutes)\/[^"?#]+)[^"]*"[^>]*>([\s\S]*?)<\/a>/gi)];
    if (civicLinks.length) {
      const rowTitle = civicLinks.find(link => link[2]!.toLowerCase() === 'agenda')?.[3]?.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim() ?? 'Municipal meeting';
      for (const link of civicLinks) {
        const url = new URL(link[1]!, baseUrl).href;
        if (seen.has(url)) continue;
        seen.add(url);
        const stamp = link[1]!.match(/_(\d{2})(\d{2})(\d{4})-/);
        const eventDate = stamp ? `${stamp[3]}-${stamp[1]}-${stamp[2]}T00:00:00.000Z` : undefined;
        const validDate = eventDate && Number.isFinite(Date.parse(eventDate)) && new Date(eventDate).toISOString().slice(0, 10) === eventDate.slice(0, 10);
        out.push({ title: rowTitle, excerpt: rowTitle, url, kind: link[2]!.toLowerCase() as 'agenda' | 'minutes', ...(validDate ? { eventDate } : {}) });
      }
      continue;
    }
    const titleMatch = row.match(/<a[^>]+href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/);
    if (!titleMatch) continue;
    const href = titleMatch[1].trim();
    const title = titleMatch[2].replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    // CivicPlus rarely exposes excerpt text in the listing; fall back
    // to the title for polarity scoring.
    if (!title) continue;
    const url = new URL(href, baseUrl).href;
    out.push({ title, url, excerpt: title });
  }
  return out;
}

async function fetchCivicPlusOne(
  cityHost: string | undefined,
  signal?: AbortSignal,
): Promise<PublicSentimentEntry[]> {
  const url = buildCivicPlusUrl(cityHost ?? '');
  if (!url) return [];
  const res = await fetchWithTimeoutCircuit('civic-plus', url, 15_000, signal);
  if (!res.ok) throw new Error(`civic-plus: HTTP ${res.status}`);
  const html = await res.text();
  const blocks = parseCivicPlusHtml(html, url);
  return blocks.map(b => buildEntry({
    sourceId: 'local-minutes',
    title: b.title,
    excerpt: b.excerpt,
    url: b.url,
  }));
}

/* ── Shared entry builder ──────────────────────────────────────────── */

/**
 * Build a normalised `PublicSentimentEntry` from a vendor-agnostic
 * (title, excerpt, url) tuple. All three local-minutes vendors funnel
 * through this so the polarity + classifier logic stays identical.
 */
function buildEntry(input: {
  sourceId: 'local-minutes';
  title: string;
  excerpt: string;
  url: string;
}): PublicSentimentEntry {
  // Combined classification surface: title + excerpt. Minutes entries
  // usually carry the dispositive verb in the title ("Approved …") but
  // some vendors stuff it in the excerpt.
  const text = `${input.title} ${input.excerpt}`.trim();
  const btmTech = classifyBtmTech(text);
  const sentimentScore = polarityScore(text);
  const confidence = lengthBasedConfidence(text);
  return {
    sourceId: input.sourceId,
    fetchedAt: new Date().toISOString(),
    btmTech,
    sentimentScore,
    confidence,
    supportingSnippet: truncateSnippet(text),
    url: input.url,
  };
}

/* ── Public entry point ────────────────────────────────────────────── */

export interface LocalMinutesFilters {
  /** Attempt-owned report cancellation/watchdog signal. */
  signal?: AbortSignal;
  /** USPS state code — drives the MuniCode client-id lookup. */
  state?: string;
  /** Granicus client subdomain slug. Optional — Granicus is skipped if absent. */
  granicusClient?: string;
  /** CivicPlus city hostname (e.g. 'www.macon.gov'). Optional. */
  civicPlusHost?: string;
}

/**
 * PSE-2 fetcher — structurally satisfies `PublicSentimentSourceFn`.
 *
 * Each (vendor, query) pair runs through its own try/catch so one
 * vendor's outage doesn't take down the other two. The orchestrator
 * sees one logical source (`'local-minutes'`); the per-vendor breakdown
 * lives in the telemetry log breadcrumbs.
 *
 * Resilience:
 *  - Missing client-id / client slug / city host → graceful empty for
 *    that vendor (no fetch, no error).
 *  - HTML shape change → `parse*Html` returns `[]`; no thrown error.
 *  - All three vendors fail → return `{ status: 'unavailable', reason }`.
 *
 * NEVER throws.
 */
export async function fetchLocalMinutesSentiment(
  queries: string[],
  filters: LocalMinutesFilters = {},
): Promise<PublicSentimentSourceResult> {
  throwIfAborted(filters.signal);
  if (queries.length === 0) {
    return { status: 'unavailable', reason: 'no queries supplied', entries: [] };
  }

  const allEntries: PublicSentimentEntry[] = [];
  const errors: string[] = [];
  let attemptCount = 0;

  for (const q of queries) {
    throwIfAborted(filters.signal);
    // MuniCode
    if (filters.state && buildMuniCodeUrl(filters.state, q)) {
      attemptCount++;
      try {
        const entries = await fetchMuniCodeOne(filters.state, q, filters.signal);
        throwIfAborted(filters.signal);
        allEntries.push(...entries);
      } catch (e) {
        rethrowIfAborted(filters.signal, e);
        const msg = (e as Error)?.message ?? String(e);
        errors.push(`municode "${q}": ${msg}`);
        // Telemetry breadcrumb — mirrors `[provider:nrel:wind]` shape.
        // !cancelled gate is N/A here (sync helper, not an async effect).
        console.warn(`[pse:local-minutes:municode] ${msg}`);
      }
    }

    // Granicus
    if (filters.granicusClient) {
      attemptCount++;
      try {
        const entries = await fetchGranicusOne(filters.granicusClient, q, filters.signal);
        throwIfAborted(filters.signal);
        allEntries.push(...entries);
      } catch (e) {
        rethrowIfAborted(filters.signal, e);
        const msg = (e as Error)?.message ?? String(e);
        errors.push(`granicus "${q}": ${msg}`);
        console.warn(`[pse:local-minutes:granicus] ${msg}`);
      }
    }
  }

  // CivicPlus is queryless — fetches the AgendaCenter index page once
  // per locality (the page itself drives free-text filtering on the
  // client). Run after the per-query loop so we don't multiplex it.
  if (filters.civicPlusHost) {
    attemptCount++;
    try {
      const entries = await fetchCivicPlusOne(filters.civicPlusHost, filters.signal);
      throwIfAborted(filters.signal);
      allEntries.push(...entries);
    } catch (e) {
      rethrowIfAborted(filters.signal, e);
      const msg = (e as Error)?.message ?? String(e);
      errors.push(`civic-plus: ${msg}`);
      console.warn(`[pse:local-minutes:civic-plus] ${msg}`);
    }
  }

  // No vendors attempted at all (no state, no client, no host) →
  // ok with empty entries (the locality is just outside our coverage).
  if (attemptCount === 0) {
    return { status: 'unavailable', reason: 'No verified municipal archive is configured for this locality; no local records were searched.', entries: [] };
  }

  // Every attempt errored → unavailable.
  if (allEntries.length === 0 && errors.length === attemptCount) {
    return { status: 'unavailable', reason: errors.join('; '), entries: [] };
  }

  // Dedup by URL.
  const seen = new Set<string>();
  const deduped: PublicSentimentEntry[] = [];
  for (const e of allEntries) {
    if (seen.has(e.url)) continue;
    seen.add(e.url);
    deduped.push(e);
  }

  return { status: 'ok', entries: deduped };
}
